import React, { useState, useCallback, useEffect } from "react";
import {
  ActivityIndicator,
  FlatList,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  View,
} from "react-native";
import { getCurrentLocation } from "@utils/getCurrentLocation";
import color from "@constants/color";
import Text from "@components/CustomText";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import toast from "@utils/toast";
import { requestLocation } from "@utils/permissions";
import { commonStyles, hubStyles as styles } from "./styles";
import { useOnboarding } from "./OnboardingContext";
import {
  getSpokeList,
  getSpokeFilters,
  submitOnboarding,
  type SpokeFilters,
} from "@services/onboardingApi";
import { useFlowNavigation } from "./useFlowNavigation";
import type { StackNavigationProp } from "@react-navigation/stack";
import type { OnboardingStackParamList, DeliveryHub } from "./types";

type LocationState = "loading" | "denied" | "ready";

interface NearestHubProps {
  navigation: StackNavigationProp<OnboardingStackParamList, "NearestHub">;
}

// ─── Shared hub list + submit logic ──────────────────────────────────────────

function useHubSubmit(
  navigation: NearestHubProps["navigation"],
  onboardingData: ReturnType<typeof useOnboarding>["data"],
  updateData: ReturnType<typeof useOnboarding>["updateData"],
) {
  const [selectedHub, setSelectedHub] = useState<DeliveryHub | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSelectHub = useCallback((hub: DeliveryHub) => {
    setSelectedHub(hub);
  }, []);

  const onContinue = useCallback(async () => {
    if (isSubmitting || !selectedHub) return;
    setIsSubmitting(true);
    try {
      updateData({ selectedSpoke: selectedHub });
      await submitOnboarding({
        spokeId: selectedHub.id,
        riderType: onboardingData.riderType,
      });
      navigation.navigate("ApplicationSubmitted", {
        spokeName: selectedHub.name,
        spokeAddress: selectedHub.address,
        fullName: onboardingData.fullName,
      });
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      toast.showLongToast(
        axiosError?.response?.data?.message ||
          "Failed to submit. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, selectedHub, onboardingData, updateData, navigation]);

  return { selectedHub, isSubmitting, onSelectHub, onContinue };
}

// ─── Hub card ─────────────────────────────────────────────────────────────────

function HubCard({
  item,
  isSelected,
  onPress,
  onPressMap,
}: {
  item: DeliveryHub;
  isSelected: boolean;
  onPress: () => void;
  onPressMap: () => void;
}) {
  return (
    <Pressable
      style={[styles.hubCard, isSelected && styles.hubCardSelected]}
      onPress={onPress}
      accessibilityRole="radio"
      accessibilityState={{ selected: isSelected }}
      accessibilityLabel={item.name}
    >
      <Text style={styles.hubName}>{item.name}</Text>
      <Text style={styles.hubAddress}>{item.address}</Text>
      <View style={styles.hubCardFooter}>
        <Pressable
          onPress={(e) => {
            e.stopPropagation();
            onPressMap();
          }}
          hitSlop={8}
          accessibilityRole="link"
          accessibilityLabel="View on Map"
        >
          <Text style={styles.viewOnMapText}>View on Map</Text>
        </Pressable>
        {item.distanceKm != null && (
          <Text style={styles.distanceBadge}>{item.distanceKm} km away</Text>
        )}
      </View>
    </Pressable>
  );
}

// ─── Geo mode ────────────────────────────────────────────────────────────────

function GeoHubSelector({
  radiusKm,
  selectedHub,
  onSelectHub,
  onOpenMap,
}: {
  radiusKm: number;
  selectedHub: DeliveryHub | null;
  onSelectHub: (hub: DeliveryHub) => void;
  onOpenMap: (hub: DeliveryHub) => void;
}) {
  const [locationState, setLocationState] = useState<LocationState>("loading");
  const [hubs, setHubs] = useState<DeliveryHub[]>([]);
  const [isLoadingHubs, setIsLoadingHubs] = useState(false);

  const fetchHubs = useCallback(
    async (lat: number, lng: number) => {
      setIsLoadingHubs(true);
      try {
        const list = await getSpokeList({
          mode: "geo",
          lat,
          lng,
          radius_km: radiusKm,
        });
        setHubs(list);
      } catch {
        toast.showLongToast("Failed to load nearby hubs. Try again.");
        setHubs([]);
      } finally {
        setIsLoadingHubs(false);
      }
    },
    [radiusKm],
  );

  const acquireLocation = useCallback(async () => {
    setLocationState("loading");
    const { granted } = await requestLocation();
    if (!granted) {
      setLocationState("denied");
      return;
    }
    try {
      const { coords } = await getCurrentLocation({ timeoutMs: 10000 });
      setLocationState("ready");
      fetchHubs(coords.latitude, coords.longitude);
    } catch {
      setLocationState("denied");
    }
  }, [fetchHubs]);

  useEffect(() => {
    acquireLocation();
  }, [acquireLocation]);

  if (locationState === "loading") {
    return (
      <View style={styles.locationStatusContainer}>
        <ActivityIndicator color={color.primary} size="large" />
        <Text style={styles.locationStatusText}>Getting your location...</Text>
      </View>
    );
  }

  if (locationState === "denied") {
    return (
      <View style={styles.locationStatusContainer}>
        <Text style={styles.locationDeniedTitle}>Location access needed</Text>
        <Text style={styles.infoMessage}>
          We use your location to show nearby delivery hubs within {radiusKm}{" "}
          km.
        </Text>
        <Pressable
          style={styles.retryButton}
          onPress={acquireLocation}
          accessibilityRole="button"
        >
          <Text style={styles.retryButtonText}>Grant Permission</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <>
      {isLoadingHubs && (
        <View style={styles.hubLoaderContainer}>
          <ActivityIndicator color={color.primary} />
        </View>
      )}
      {!isLoadingHubs && hubs.length === 0 && (
        <Text style={styles.infoMessage}>
          No delivery hubs found within {radiusKm} km of your location.
        </Text>
      )}
      {hubs.length > 0 && (
        <>
          <Text variant="title_16" style={styles.hubListTitle}>
            Nearest Delivery Hubs
          </Text>
          <FlatList
            data={hubs}
            renderItem={({ item }) => (
              <HubCard
                item={item}
                isSelected={selectedHub?.id === item.id}
                onPress={() => onSelectHub(item)}
                onPressMap={() => onOpenMap(item)}
              />
            )}
            keyExtractor={(item) => item.id}
            showsVerticalScrollIndicator={false}
            scrollEnabled={false}
          />
        </>
      )}
    </>
  );
}

// ─── State/city mode ─────────────────────────────────────────────────────────

function StateCityHubSelector({
  selectedHub,
  onSelectHub,
  onOpenMap,
}: {
  selectedHub: DeliveryHub | null;
  onSelectHub: (hub: DeliveryHub) => void;
  onOpenMap: (hub: DeliveryHub) => void;
}) {
  const [filters, setFilters] = useState<SpokeFilters | null>(null);
  const [selectedState, setSelectedState] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [hubs, setHubs] = useState<DeliveryHub[]>([]);
  const [isLoadingHubs, setIsLoadingHubs] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  useEffect(() => {
    getSpokeFilters()
      .then(setFilters)
      .catch(() =>
        toast.showLongToast("Failed to load location filters. Try again."),
      );
  }, []);

  const onPickState = useCallback((state: string) => {
    setSelectedState(state);
    setSelectedCity("");
    setHubs([]);
    setShowStateDropdown(false);
  }, []);

  const onPickCity = useCallback(
    async (city: string) => {
      setSelectedCity(city);
      setShowCityDropdown(false);
      if (!selectedState) return;
      setIsLoadingHubs(true);
      try {
        const list = await getSpokeList({
          mode: "state_city",
          state: selectedState,
          city,
        });
        setHubs(list);
      } catch {
        toast.showLongToast("Failed to load hubs. Try again.");
        setHubs([]);
      } finally {
        setIsLoadingHubs(false);
      }
    },
    [selectedState],
  );

  const cities = selectedState
    ? filters?.cities_by_state[selectedState] ?? []
    : [];

  return (
    <>
      {/* State dropdown */}
      <Pressable
        style={styles.dropdownContainer}
        onPress={() => {
          setShowStateDropdown((v) => !v);
          setShowCityDropdown(false);
        }}
        accessibilityRole="button"
        accessibilityLabel="Select state"
      >
        <View style={styles.dropdownFlex}>
          {selectedState ? (
            <>
              <Text style={styles.dropdownLabel}>State</Text>
              <Text variant="body_text_3" style={styles.dropdownValue}>
                {selectedState}
              </Text>
            </>
          ) : (
            <Text variant="body_text_3" style={styles.dropdownPlaceholder}>
              Select State
            </Text>
          )}
        </View>
        <Text style={styles.dropdownChevron}>
          {showStateDropdown ? "▲" : "▼"}
        </Text>
      </Pressable>

      {showStateDropdown && filters && (
        <View style={styles.dropdownList}>
          {filters.states.map((state, idx) => (
            <Pressable
              key={state}
              style={[
                styles.dropdownListItem,
                idx === filters.states.length - 1 &&
                  styles.dropdownListItemLast,
              ]}
              onPress={() => onPickState(state)}
              accessibilityRole="menuitem"
            >
              <Text variant="body_text_3" style={styles.dropdownListItemText}>
                {state}
              </Text>
            </Pressable>
          ))}
        </View>
      )}

      {/* City dropdown */}
      <Pressable
        style={styles.dropdownContainer}
        onPress={() => {
          if (selectedState) {
            setShowCityDropdown((v) => !v);
            setShowStateDropdown(false);
          }
        }}
        accessibilityRole="button"
        accessibilityLabel="Select city"
      >
        <View style={styles.dropdownFlex}>
          {selectedCity ? (
            <>
              <Text style={styles.dropdownLabel}>City</Text>
              <Text variant="body_text_3" style={styles.dropdownValue}>
                {selectedCity}
              </Text>
            </>
          ) : (
            <Text variant="body_text_3" style={styles.dropdownPlaceholder}>
              {selectedState ? "Select City" : "Select state first"}
            </Text>
          )}
        </View>
        <Text style={styles.dropdownChevron}>
          {showCityDropdown ? "▲" : "▼"}
        </Text>
      </Pressable>

      {showCityDropdown && cities.length > 0 && (
        <View style={styles.dropdownList}>
          {cities.map((city, idx) => (
            <Pressable
              key={city}
              style={[
                styles.dropdownListItem,
                idx === cities.length - 1 && styles.dropdownListItemLast,
              ]}
              onPress={() => onPickCity(city)}
              accessibilityRole="menuitem"
            >
              <Text variant="body_text_3" style={styles.dropdownListItemText}>
                {city}
              </Text>
            </Pressable>
          ))}
        </View>
      )}

      {isLoadingHubs && (
        <View style={styles.hubLoaderContainer}>
          <ActivityIndicator color={color.primary} />
        </View>
      )}

      {!isLoadingHubs && selectedCity && hubs.length === 0 && (
        <Text style={styles.infoMessage}>
          No hubs found in {selectedCity}, {selectedState}.
        </Text>
      )}

      {hubs.length > 0 && (
        <>
          <Text variant="title_16" style={styles.hubListTitle}>
            Nearest Delivery Hubs
          </Text>
          <FlatList
            data={hubs}
            renderItem={({ item }) => (
              <HubCard
                item={item}
                isSelected={selectedHub?.id === item.id}
                onPress={() => onSelectHub(item)}
                onPressMap={() => onOpenMap(item)}
              />
            )}
            keyExtractor={(item) => item.id}
            showsVerticalScrollIndicator={false}
            scrollEnabled={false}
          />
        </>
      )}
    </>
  );
}

// ─── Screen ───────────────────────────────────────────────────────────────────

const NearestHub: React.FC<NearestHubProps> = ({ navigation }) => {
  const { data: onboardingData, updateData } = useOnboarding();
  const { getStepConfig } = useFlowNavigation();
  const cfg = getStepConfig<"NearestHub">();
  const filterMode = cfg?.filter_mode ?? "geo";
  const radiusKm = cfg?.radius_km ?? 50;

  const { selectedHub, isSubmitting, onSelectHub, onContinue } = useHubSubmit(
    navigation,
    onboardingData,
    updateData,
  );

  const openMap = useCallback((hub: DeliveryHub) => {
    const query =
      hub.lat != null && hub.lng != null
        ? `${hub.lat},${hub.lng}`
        : encodeURIComponent(hub.address);
    const url =
      Platform.select({
        ios: `maps://?q=${encodeURIComponent(hub.name)}&ll=${hub.lat ?? ""},${hub.lng ?? ""}`,
        android: `geo:${hub.lat ?? 0},${hub.lng ?? 0}?q=${query}`,
      }) ?? `https://www.google.com/maps/search/?api=1&query=${query}`;
    Linking.openURL(url).catch(() => {
      Linking.openURL(
        `https://www.google.com/maps/search/?api=1&query=${query}`,
      );
    });
  }, []);

  return (
    <>
      <View style={commonStyles.container}>
        <HeaderWithBackAndTitle
          headertitle="Nearest Hub"
          navigation={navigation}
          hideShadow
        />

        <ScrollView
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={styles.hubScrollContent}
        >
          {filterMode === "geo" ? (
            <GeoHubSelector
              radiusKm={radiusKm}
              selectedHub={selectedHub}
              onSelectHub={onSelectHub}
              onOpenMap={openMap}
            />
          ) : (
            <StateCityHubSelector
              selectedHub={selectedHub}
              onSelectHub={onSelectHub}
              onOpenMap={openMap}
            />
          )}
        </ScrollView>

        <View style={styles.bottomBar}>
          <Pressable
            style={[
              commonStyles.primaryButton,
              styles.continueButton,
              !selectedHub && commonStyles.primaryButtonDisabled,
            ]}
            disabled={isSubmitting || !selectedHub}
            onPress={onContinue}
            accessibilityRole="button"
            accessibilityLabel="Continue"
          >
            {isSubmitting ? (
              <ActivityIndicator color={color.white} />
            ) : (
              <Text variant="title_16" style={commonStyles.primaryButtonText}>
                Continue
              </Text>
            )}
          </Pressable>
        </View>
      </View>
    </>
  );
};

export default NearestHub;
